/**
 * Participant list API contracts for event-scoped staff views.
 */

import type { PhotoFraming } from './photoFraming'
import { apiClient } from './client'
import type { TryoutRegistrationQuestion } from './tryoutRegistration'
import type {
  TryoutSetupClub,
  TryoutSetupEvent,
  TryoutSetupSeason,
} from './tryoutSetup'

export interface ParticipantListRole {
  id: string
  name: string
  abbreviation?: string
  shortCode?: string
  shortName?: string
}

export interface ParticipantListParticipant {
  userId?: string
  id: string
  participantNumber?: string | number
  firstName?: string
  preferredName?: string
  lastName?: string
  emailAddress?: string
  email?: string
  phoneNumber?: string
  dateOfBirth?: string
  clubName?: string
  parentName?: string
  parentEmail?: string
  parentPhone?: string
  photoFraming?: PhotoFraming | null
  profileImage?: string
  profileImageUrl?: string
  photoUrl?: string
  imageConsent?: boolean
  junior?: boolean
  rolePreferences?: Array<string | ParticipantListRole>
  prefRole1?: string
  prefRole2?: string
  registrationStatus?: string
  assessmentEligibility?: { current: boolean; sessions: Record<string, boolean | null>; historyKnown: boolean; summarySessionId?: string | null }
  offer?: string
  offerRole?: string
  offerResponse?: string
  attended?: number | string
  deskProcessed?: boolean | number
  archived?: boolean
  bypassPhoto?: boolean
  bypassCollection?: boolean
  accountVerified?: boolean
  generated?: boolean
  attributes?: Record<string, unknown>
  responses?: Record<string, unknown>
  attendance?: ParticipantListAttendanceSummary
}

export interface ParticipantListAttendanceSummary {
  attendedCount?: number
  absentCount?: number
  unknownCount?: number
  totalSessions?: number
  summary?: string
  sessions?: ParticipantListAttendanceSession[]
}

export interface ParticipantListAttendanceSession {
  sessionId: string
  sessionTitle?: string
  startTime?: string
  status?: 'attended' | 'absent' | 'unknown' | string
  attended?: boolean | number
  reason?: string
}

export interface ParticipantListSession {
  id: string
  title: string
  startTime?: string
  startDateTime?: string
}

export interface ParticipantListSummary {
  totalRows?: number
  activeRows?: number
  archivedRows?: number
  completedRegistrations?: number
  incompleteRegistrations?: number
}

export interface ParticipantListResponse {
  permissions?: { canCreateParticipant: boolean; canUpdateRegistration?: boolean; canReviewManualAssociations?: boolean; canCorrectCutDecisions?: boolean }
  club: TryoutSetupClub
  season: TryoutSetupSeason
  event: TryoutSetupEvent
  participants: ParticipantListParticipant[]
  roles?: ParticipantListRole[]
  questions?: TryoutRegistrationQuestion[]
  summary?: ParticipantListSummary
}

export interface ParticipantListParticipantPatch {
  firstName?: string
  preferredName?: string
  lastName?: string
  prefRole1?: string | null
  prefRole2?: string | null
}

export interface ParticipantListEmailChangeRequest {
  email: string
}

export interface ParticipantListEmailChangeResponse {
  status: 'new_email_verification_sent' | 'existing_email_confirmation_sent' | 'user_already_in_event' | 'link_confirmation_required' | string
  message?: string
  matchingName?: string
  tokenId?: string
}

export interface ParticipantEmailMatch {
  id: string
  firstName: string
  lastName: string
  preferredName: string
  email: string
  alreadyRegistered: boolean
}

export interface ParticipantNameCandidate {
  id: string
  firstName: string
  lastName: string
  preferredName: string
  photoUrl: string | null
  alreadyRegistered: boolean
  events: { id: string; name: string }[]
}
export interface ParticipantNameMatch { index: number; candidates: ParticipantNameCandidate[] }

export interface ManualParticipantAssociation {
  id: string
  participantNumber: number | string
  firstName: string
  lastName: string
  currentUserId: string
  currentUserName: string
  expectedVersion: string
  candidates: ParticipantNameCandidate[]
}
export interface ManualParticipantAssociationReview {
  participants: ManualParticipantAssociation[]
  nextCursor: string | null
}

export interface CutDecisionEntry {
  id: string; sequence: number; kind: 'cut' | 'recall' | 'withdrawal' | 'reinstate' | 'correction'
  actorId: string; actorName: string | null; recordedAt: string; effectiveAt: string
  sourceSessionId: string | null; correctsId: string | null; correctionAction: 'replace' | 'void' | null; reason: string | null
}
export interface CutDecisionHistory {
  participantId: string; offer: string | null; version: number; entries: CutDecisionEntry[]
  sessions: { id: string; title: string; startsAt: string }[]
  eligibility: NonNullable<ParticipantListParticipant['assessmentEligibility']>
}
export interface CutDecisionCorrection {
  decisionId: string; expectedVersion: number; action: 'replace' | 'void'; sourceSessionId?: string; reason: string
}

export class ParticipantListAPI {
  updateEmailAsSystemAdmin(seasonId:string,eventId:string,participantId:string,request:{email:string;expectedEmail:string|null;expectedUserId:string},clubId:string) {
    return apiClient.patch<{status:'updated';participantId:string;userId:string;email:string}>(`/v2/season/${seasonId}/tryouts/${eventId}/participants/${participantId}/email`,request,{clubId})
  }

  async loadCutDecisionHistory(seasonId: string, tryoutId: string, participantId: string, clubId: string, signal?: AbortSignal): Promise<CutDecisionHistory> {
    return apiClient.get(`/v2/season/${seasonId}/tryouts/${tryoutId}/participants/${participantId}/loadCutDecisionHistory`, { clubId }, signal)
  }
  async correctCutDecision(seasonId: string, tryoutId: string, participantId: string, request: CutDecisionCorrection, clubId: string): Promise<{version: number; entryId: string; eligibility: CutDecisionHistory['eligibility']}> {
    return apiClient.post(`/v2/season/${seasonId}/tryouts/${tryoutId}/participants/${participantId}/correctCutDecision`, request, { clubId })
  }

  async loadManualAssociations(seasonId: string, tryoutId: string, clubId: string, after?: string, signal?: AbortSignal): Promise<ManualParticipantAssociationReview> {
    return apiClient.get(`/v2/season/${seasonId}/tryouts/${tryoutId}/participants/loadManualAssociations`, { clubId, ...(after ? { after } : {}) }, signal)
  }
  async associateHistoricalUser(seasonId: string, tryoutId: string, participantId: string, request: { targetUserId: string; expectedUserId: string; expectedVersion: string }, clubId: string): Promise<{ participant: ParticipantListParticipant }> {
    return apiClient.post(`/v2/season/${seasonId}/tryouts/${tryoutId}/participants/${participantId}/associateHistoricalUser`, request, { clubId })
  }
  async loadNameMatches(seasonId: string, tryoutId: string, names: { firstName: string; lastName: string }[], clubId: string): Promise<{ matches: ParticipantNameMatch[] }> {
    return apiClient.post(`/v2/season/${seasonId}/tryouts/${tryoutId}/participants/loadNameMatches`, { names }, { clubId })
  }
  async loadEmailMatch(seasonId: string, tryoutId: string, email: string, clubId: string): Promise<{ match: ParticipantEmailMatch | null }> {
    return apiClient.post(`/v2/season/${seasonId}/tryouts/${tryoutId}/participants/loadEmailMatch`, { email }, { clubId })
  }
  async createParticipant(seasonId: string, tryoutId: string, request: ParticipantListParticipantPatch & { firstName: string; lastName: string; email?: string; existingUserId?: string; associationSource?: 'club-name' }, clubId: string): Promise<{ participant: ParticipantListParticipant }> {
    return apiClient.post(`/v2/season/${seasonId}/tryouts/${tryoutId}/participants`, request, { clubId })
  }
  async loadParticipants(
    seasonId: string,
    tryoutId: string,
    clubId?: string,
    signal?: AbortSignal
  ): Promise<ParticipantListResponse> {
    const query = clubId ? { clubId } : undefined
    return apiClient.get(`/v2/season/${seasonId}/tryouts/${tryoutId}/participants`, query, signal)
  }

  async updateParticipant(
    seasonId: string,
    tryoutId: string,
    participantId: string,
    request: ParticipantListParticipantPatch,
    clubId?: string
  ): Promise<ParticipantListParticipant> {
    const query = clubId ? { clubId } : undefined
    return apiClient.patch(`/v2/season/${seasonId}/tryouts/${tryoutId}/participants/${participantId}`, request, query)
  }

  async updateParticipantNumber(
    seasonId: string,
    tryoutId: string,
    participantId: string,
    participantNumber: number,
    clubId?: string
  ): Promise<ParticipantListParticipant> {
    const query = clubId ? { clubId } : undefined
    return apiClient.patch(`/v2/season/${seasonId}/tryouts/${tryoutId}/participants/${participantId}/number`, { participantNumber }, query)
  }

  async updateAttendance(
    seasonId: string,
    tryoutId: string,
    participantId: string,
    sessionId: string,
    request: { attended: -1 | 0 | 1, reason?: string },
    clubId?: string
  ): Promise<ParticipantListParticipant> {
    const query = clubId ? { clubId } : undefined
    return apiClient.patch(`/v2/season/${seasonId}/tryouts/${tryoutId}/participants/${participantId}/attendance/${sessionId}`, request, query)
  }

  async updateRegistrationStatus(seasonId: string, tryoutId: string, participantId: string, registrationStatus: 'complete' | 'incomplete', clubId: string): Promise<ParticipantListParticipant> {
    return apiClient.patch(`/v2/season/${seasonId}/tryouts/${tryoutId}/participants/${participantId}/registration-status`, { registrationStatus }, { clubId })
  }

  async updateDeskProcessed(
    seasonId: string,
    tryoutId: string,
    participantId: string,
    deskProcessed: boolean,
    clubId?: string
  ): Promise<ParticipantListParticipant> {
    const query = clubId ? { clubId } : undefined
    return apiClient.patch(`/v2/season/${seasonId}/tryouts/${tryoutId}/participants/${participantId}/desk-processed`, { deskProcessed }, query)
  }

  async updateWithdrawn(
    seasonId: string,
    tryoutId: string,
    participantId: string,
    withdrawn: boolean,
    clubId?: string
  ): Promise<ParticipantListParticipant> {
    const query = clubId ? { clubId } : undefined
    return apiClient.patch(`/v2/season/${seasonId}/tryouts/${tryoutId}/participants/${participantId}/withdrawn`, { withdrawn }, query)
  }

  async deleteParticipant(
    seasonId: string,
    tryoutId: string,
    participantId: string,
    clubId?: string
  ): Promise<void> {
    const query = clubId ? { clubId } : undefined
    await apiClient.delete(`/v2/season/${seasonId}/tryouts/${tryoutId}/participants/${participantId}`, query)
  }

  async requestEmailChange(
    seasonId: string,
    tryoutId: string,
    participantId: string,
    request: ParticipantListEmailChangeRequest,
    clubId?: string
  ): Promise<ParticipantListEmailChangeResponse> {
    const query = clubId ? { clubId } : undefined
    return apiClient.post(`/v2/season/${seasonId}/tryouts/${tryoutId}/participants/${participantId}/email-change/request`, request, query)
  }

  async confirmEmailLink(
    seasonId: string,
    tryoutId: string,
    participantId: string,
    tokenId: string,
    clubId?: string
  ): Promise<ParticipantListEmailChangeResponse> {
    const query = clubId ? { clubId } : undefined
    return apiClient.post(`/v2/season/${seasonId}/tryouts/${tryoutId}/participants/${participantId}/email-change/confirm-link`, { tokenId }, query)
  }
}

export const participantListAPI = new ParticipantListAPI()
